import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from './ui/alert-dialog';
import { Download, RefreshCw, Users, Trash2, MessageCircle, Phone } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/lead-submissions`);
      if (response.data.success) {
        setSubmissions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Ralat mendapatkan data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteSingle = async (id, name) => {
    try {
      setDeleting(true);
      const response = await axios.delete(`${API}/lead-submissions/${id}`);
      if (response.data.success) {
        toast.success(`Lead "${name}" berjaya dipadamkan`);
        fetchSubmissions();
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Ralat memadam lead');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    try {
      setDeleting(true);
      const response = await axios.delete(`${API}/lead-submissions`);
      if (response.data.success) {
        toast.success(`${response.data.deleted_count} lead berjaya dipadamkan`);
        fetchSubmissions();
      }
    } catch (error) {
      console.error('Error deleting all:', error);
      toast.error('Ralat memadam semua lead');
    } finally {
      setDeleting(false);
    }
  };

  const exportToCSV = () => {
    if (submissions.length === 0) {
      toast.error('Tiada data untuk export');
      return;
    }

    const headers = ['Tarikh & Masa', 'Nama Penuh', 'No Telefon', 'Status Pekerjaan'];
    const rows = submissions.map(sub => [
      new Date(sub.timestamp).toLocaleString('ms-MY'),
      sub.full_name,
      sub.phone,
      sub.employment_status
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `leads-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Data berjaya diexport!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ms-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openWhatsApp = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    const formattedPhone = cleanPhone.startsWith('0') ? '60' + cleanPhone.slice(1) : cleanPhone;
    window.open(`https://wa.me/${formattedPhone}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-emerald-600 animate-spin mx-auto mb-4" />
          <p className="text-emerald-900 font-semibold">Memuatkan data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-emerald-900 mb-2">Admin Dashboard</h1>
          <p className="text-emerald-700 text-sm sm:text-base">Muhd Aiman Financial Consultant - Pengurusan Lead</p>
        </div>

        {/* Stats Card */}
        <Card className="mb-6 sm:mb-8 border-t-4 border-amber-500">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-emerald-100 p-3 sm:p-4 rounded-full">
                  <Users className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-emerald-600 font-medium">Jumlah Lead</p>
                  <p className="text-2xl sm:text-3xl font-bold text-emerald-900" data-testid="total-leads-count">
                    {submissions.length}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <Button
                  onClick={fetchSubmissions}
                  disabled={refreshing}
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="refresh-button"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  onClick={exportToCSV}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="export-csv-button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
                {submissions.length > 0 && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        disabled={deleting}
                        className="bg-red-500 hover:bg-red-600 text-white"
                        data-testid="delete-all-button"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Padam Semua
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Padam Semua Lead?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tindakan ini akan memadamkan SEMUA {submissions.length} lead dari database. 
                          Data yang dipadam tidak boleh dikembalikan.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleDeleteAll}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          Ya, Padam Semua
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl text-emerald-900">Senarai Lead</CardTitle>
            <CardDescription>Semua permohonan semakan kelayakan yang diterima</CardDescription>
          </CardHeader>
          <CardContent>
            {submissions.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-16 w-16 text-emerald-300 mx-auto mb-4" />
                <p className="text-emerald-600 text-lg">Tiada lead lagi</p>
                <p className="text-emerald-500 text-sm mt-2">Lead baru akan muncul di sini</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-emerald-50">
                      <TableHead className="font-bold text-emerald-900">Tarikh & Masa</TableHead>
                      <TableHead className="font-bold text-emerald-900">Nama Penuh</TableHead>
                      <TableHead className="font-bold text-emerald-900">No Telefon</TableHead>
                      <TableHead className="font-bold text-emerald-900">Status Pekerjaan</TableHead>
                      <TableHead className="font-bold text-emerald-900 text-right">Tindakan</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {submissions.map((sub, index) => (
                      <TableRow key={sub.id || index} className="hover:bg-emerald-50/50">
                        <TableCell className="text-emerald-700 text-sm">{formatDate(sub.timestamp)}</TableCell>
                        <TableCell className="font-medium text-emerald-900">{sub.full_name}</TableCell>
                        <TableCell className="text-emerald-700">{sub.phone}</TableCell>
                        <TableCell>
                          <span className="inline-block px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800">
                            {sub.employment_status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openWhatsApp(sub.phone)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              data-testid={`whatsapp-button-${sub.id}`}
                              title="Hubungi via WhatsApp"
                            >
                              <MessageCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => window.location.href = `tel:${sub.phone}`}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`call-button-${sub.id}`}
                              title="Panggilan telefon"
                            >
                              <Phone className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-button-${sub.id}`}
                                  title="Padam lead"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Padam Lead?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Anda pasti mahu padam lead <strong>{sub.full_name}</strong> ({sub.phone})?
                                    Tindakan ini tidak boleh dikembalikan.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Batal</AlertDialogCancel>
                                  <AlertDialogAction 
                                    onClick={() => handleDeleteSingle(sub.id, sub.full_name)}
                                    className="bg-red-500 hover:bg-red-600"
                                  >
                                    Ya, Padam
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Back to Landing Page */}
        <div className="mt-6 sm:mt-8 text-center">
          <a href="/" className="text-emerald-600 hover:text-emerald-800 font-medium">
            ← Kembali ke Landing Page
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
